import { useEffect, useRef } from "react";
// client/guest/utils.ts
export type Step =
  | "home"
  | "event"
  | "passes"   // список пропусков
  | "eula"
  | "kinds"
  | "subkind"
  | "payment"
  | "pending"  // <- промежуточный статус подтверждения оплаты/заявки
  | "pass"
  | "done"
  | "noevent"
  | "error";

export const normalizeStep = (s?: string | null): Step | null => {
  const v = String(s || "").toLowerCase();
  return [
    "home",
    "event",
    "passes",
    "eula",
    "kinds",
    "subkind",
    "payment",
    "pending",
    "pass",
    "done",
    "noevent",
    "error",
  ].includes(v as any)
    ? (v as Step)
    : null;
};

export type TicketSubkind = {
  id: number;
  title: string;
  description?: string | null;
  price_rub: number | null;
  position?: number | null;
  disabled?: boolean;
  available_at?: string | null;
};

export type TicketKind = {
  id: number;
  title: string;
  description?: string | null;
  price_rub: number | null;
  position?: number | null;
  subkinds?: TicketSubkind[];
  disabled?: boolean;
  available_at?: string | null;
};

export const isFuture = (iso?: string | null) => {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t > Date.now();
};

export const isKindDisabled = (k?: TicketKind | null): boolean =>
  !!k && (!!k.disabled || isFuture(k.available_at));

export const isSubDisabled = (s?: TicketSubkind | null): boolean =>
  !!s && (!!s.disabled || isFuture(s.available_at));

export const fmtMoney = (n: number) => new Intl.NumberFormat("ru-RU").format(n);

export type EventLite = {
  id: number;
  title: string;
  start_at?: string | null;
  end_at?: string | null;
  venue?: string | null;
  currency?: string | null;
};

export const fmtDateTime = (iso?: string | null) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(t));
  } catch {
    return new Date(t).toLocaleString();
  }
};

export const fmtDateShort = (iso?: string | null) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

// --- helpers reused across GuestFlow ---
export const lsKey = (eventId: number | null, part: "kind" | "sub") =>
  eventId ? `gf:${eventId}:${part}` : null;

// Centralized realtime/on-focus/ws + polling sync to shrink GuestFlow
export function useOnboardingSync(
  api: {
    onboarding: { get: (eventId?: number | null) => Promise<{ ok: boolean; step: any }>; set: (s: any, eventId?: number | null) => Promise<any> };
    me: () => Promise<{ ok: boolean; me?: { id: number } | null }>;
  },
  step: Step,
  eventId: number | null,
  setStep: (s: Step) => void,
  setHomePassReady: (v: boolean) => void
) {
  const pollRef = useRef<number | null>(null);
  const lastFetchRef = useRef<number>(0);

  useEffect(() => {
    // Helper: do one immediate refresh of onboarding and reflect UI
    const refreshOnce = async () => {
      try {
        // throttle to not hammer when multiple events fire at once
        const now = Date.now();
        if (now - lastFetchRef.current < 600) return;
        lastFetchRef.current = now;

        // touch /me to warm cookies/session on Android WebView (sometimes drops headers)
        try { await api.me().catch(() => null as any); } catch {}

        const ob = await api.onboarding.get(eventId ?? undefined).catch(() => null as any);
        const s = normalizeStep((ob as any)?.step);
        if (!s) return;

        // Обновляем индикатор для главной
        if (s === "pass" || s === "done") {
          setHomePassReady(true);
        } else {
          setHomePassReady(false);
        }

        // Автопереход в pass/done только из состояний оплаты/ожидания,
        // чтобы не перебивать ручные шаги "home" / "event" / "passes" и т.п.
        const isPassLike = s === "pass" || s === "done";
        const isPayLike = step === "payment" || step === "pending";

        if (isPassLike && isPayLike) {
          setStep(s);
        }

        // Если пропуск был использован, переводим экран на "done"
        if (s === "done" && step === "pass") {
          setStep("done");
        }
      } catch {}
    };

    // Decide whether we need polling for the current step
    const needPolling = () => {
      // While awaiting payment/approval, or when user is on pass screen
      return step === "payment" || step === "pending" || step === "pass";
    };

    // (Re)start polling if needed
    const startPolling = () => {
      if (!needPolling()) return;
      if (pollRef.current) return; // already running
      // First immediate attempt when entering
      refreshOnce();
      pollRef.current = window.setInterval(refreshOnce, 2000) as unknown as number;
    };

    const stopPolling = () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    // Visibility/focus listeners: refresh when app becomes visible again
    const onVisible = () => {
      // quick refresh on return from external Tinkoff page
      refreshOnce();
      // ensure polling is on if we still need it
      if (needPolling() && !pollRef.current) startPolling();
    };

    // Manage lifecycle for current step
    if (needPolling()) startPolling(); else stopPolling();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      stopPolling();
    };
  }, [step, eventId, api, setStep, setHomePassReady]);
}

// --- data helpers to slim GuestFlow ---
export type PassStatus = { event: EventLite; step: Step | null };

/** Fetch list of active events; gracefully fallback to /event/active */
export async function fetchActiveEvents(api: any): Promise<EventLite[]> {
  try {
    const list = await api.events.list().catch(() => [] as any[]);

    // считаем "активными" только события со статусом active,
    // у которых ещё не истёк ends_at (или дата окончания не задана)
    const now = Date.now();
    const activeOnly = (Array.isArray(list) ? list : []).filter((e: any) => {
      const status = String(e?.status || "").toLowerCase();
      if (status !== "active") return false;

      const rawEnd =
        e.ends_at ??
        e.end_at ??
        e.endsAt ??
        e.endAt ??
        null;

      if (!rawEnd) return true; // нет даты окончания — считаем активным

      const t = Date.parse(rawEnd);
      if (!Number.isFinite(t)) return true; // битая дата — не режем по ней

      return t >= now; // только те, что ещё не закончились
    });
    if (activeOnly.length > 0) {
      return activeOnly.map((e: any) => ({
        id: Number(e.id),
        title: String(e.title || `Событие #${e.id}`),
        start_at: e.starts_at ?? e.start_at ?? null,
        end_at: e.ends_at ?? e.end_at ?? null,
        venue: e.venue ?? e.place ?? null,
        currency: e.currency ?? "₽",
      }));
    }
  } catch {}

  // fallback to single active event endpoint
  try {
    const active = await api.event.active().catch(() => null);
    const ev = active?.event || null;
    if (ev) {
      const anyEv: any = ev as any;
      return [{
        id: Number(anyEv.id),
        title: String(anyEv.title || anyEv.name || `Событие #${anyEv.id}`),
        start_at: anyEv.starts_at ?? anyEv.start_at ?? null,
        end_at: anyEv.ends_at ?? anyEv.end_at ?? null,
        venue: anyEv.venue ?? anyEv.place ?? null,
        currency: anyEv.currency ?? "₽",
      }];
    }
  } catch {}

  return [];
}

/** Load one event by id and normalize ticket kinds/subkinds */
export async function fetchEventKinds(api: any, id: number): Promise<{ currency: string; kinds: TicketKind[]; eventId: number; eula_required: boolean; eula_text: string } | null> {
  const ev: any = await api.events.get(id).catch(() => null);
  if (!ev) return null;

  const sortBy = <T extends { position?: number | null; id: number }>(arr: T[]) =>
    [...arr].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id);

  const rawKinds: any[] = Array.isArray(ev.options) ? ev.options : [];
  const kinds: TicketKind[] = sortBy(
    rawKinds.slice(0, 5).map((k: any) => {
      const rawSubs: any[] = Array.isArray(k.suboptions)
        ? k.suboptions
        : Array.isArray(k.subkinds)
        ? k.subkinds
        : [];
      return {
        id: Number(k.id),
        title: String(k.title || k.name || "Без названия"),
        description: k.description ?? null,
        price_rub: k.price_rub ?? null,
        position: k.position ?? null,
        disabled: !!k.disabled,
        available_at: k.available_at ?? null,
        subkinds: sortBy(
          rawSubs.slice(0, 5).map((s: any) => ({
            id: Number(s.id),
            title: String(s.title || s.name || "Подвид"),
            description: s.description ?? null,
            price_rub: s.price_rub ?? null,
            position: s.position ?? null,
            disabled: !!s.disabled,
            available_at: s.available_at ?? null,
          }))
        ),
      } as TicketKind;
    })
  );
  
  const currency = String(ev.currency || "₽");
  const eula_required = !!(ev.eula_required ?? false);
  const eula_text = String(ev.eula_text ?? "");

  return { currency, kinds, eventId: id, eula_required, eula_text };
}

/** For each event, fetch onboarding step to know if pass is ready */
export async function fetchPassStatuses(api: any, events: EventLite[]): Promise<PassStatus[]> {
  const out: PassStatus[] = [];
  const base = events.length ? events : await fetchActiveEvents(api as any);
  for (const ev of base) {
    // фиксируем контекст события на сервере (но основное — event_id в /onboarding/get)
    await api.events.get(ev.id).catch(() => null);
    const ob = await api.onboarding.get(ev.id).catch(() => null as any);
    const s = normalizeStep((ob as any)?.step);
    out.push({ event: ev, step: s });
  }
  return out;
}
